import { useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { HeaderNav } from "@/components/HeaderNav";
import { HeroKinetic } from "@/components/HeroKinetic";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { DropCountdown } from "@/components/DropCountdown";
import { ProductCatalog } from "@/components/ProductCatalog";
import { LookbookSection } from "@/components/LookbookSection";
import { ProductDetailModal } from "@/components/ProductDetailModal";
import { ContactSection } from "@/components/ContactSection";
import { Footer } from "@/components/Footer";
import { Reveal, SectionKicker } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/constants";

const HowItWorks = () => {
  const { t } = useLang();
  return (
    <section id="so-gehts" data-testid="how-it-works-section" className="border-t border-edge bg-paperalt">
      <div className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24 md:px-8 md:py-32">
        <Reveal>
          <SectionKicker>{t.how.kicker}</SectionKicker>
          <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl" data-testid="how-title">
            {t.how.title}
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-px border border-edge bg-edge md:grid-cols-3">
          {t.how.steps.map(([title, text], i) => (
            <Reveal key={i} delay={i * 0.12} className="bg-surface">
              <div className="group flex h-full flex-col p-8 transition-colors duration-500 hover:bg-surface-hover">
                <span className="font-mono text-4xl font-bold text-edge-bright transition-colors duration-500 group-hover:text-volt">
                  0{i + 1}
                </span>
                <h3 className="mt-6 font-grotesk text-lg font-semibold tracking-tight text-ink sm:text-xl">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mutedink">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function HomePage() {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);
  const [prefill, setPrefill] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const handleEmail = useCallback(
    (product) => {
      setSelected(null);
      setPrefill(`${t.contact.prefill}: "${product.title}" [${product.code}] — `);
      setTimeout(() => scrollToId("kontakt"), 60);
    },
    [t]
  );

  const consumePrefill = useCallback(() => setPrefill(""), []);
  const handleOrdered = useCallback(() => setRefreshKey((k) => k + 1), []);

  return (
    <div className="App">
      <div className="grain-overlay" />
      <HeaderNav />
      <main>
        <HeroKinetic />
        <EditorialMarquee />
        <DropCountdown />
        <ProductCatalog onOpen={setSelected} refreshKey={refreshKey} />
        <LookbookSection onOpen={setSelected} />
        <HowItWorks />
        <ContactSection prefill={prefill} onPrefillConsumed={consumePrefill} />
      </main>
      <Footer />
      <AnimatePresence>
        {selected && (
          <ProductDetailModal
            product={selected}
            onClose={() => setSelected(null)}
            onEmail={handleEmail}
            onOrdered={handleOrdered}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
